import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
var _templateObject, _templateObject2, _templateObject3, _templateObject4;
function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }
import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { LinkButton } from '@adsk/alloy-react-button';
import { safeSearchRegex } from '@adsk/alloy-react-helpers';
import { MagnifyingGlassIcon, XIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import TextInput from '@adsk/alloy-react-text-input';
import theme from '@adsk/alloy-react-theme';
import ProjectRowLoader from './ProjectRowLoader';
import ProjectsError from './ProjectsError';
import VirtualizedProjectList from './VirtualizedProjectList';
import { ANALYTICS_TYPES, ANALYTICS_DEBOUNCE } from '../helpers/analytics';
import { accountsListShape, projectsListShape } from '../helpers/propTypes';
import { useDebounce } from '../hooks/useDebounce';
const ProjectPicker = _ref => {
  var _T$translate;
  let {
    accounts,
    currentAccountId,
    currentProjectId,
    projects,
    templates,
    currentProductId,
    projectsLoading,
    projectsError,
    projectsRetry,
    onClose,
    onAccountClick,
    onProjectClick
  } = _ref;
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("../i18n/".concat(locale, ".json"));
  });
  const searchRef = useRef(null);
  const [search, setSearch] = useState('');
  const reportSearchOnChangeAnalyticFn = useMemo(() => debounceSearch => {
    if (debounceSearch && debounceSearch !== '') {
      analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_SEARCH_CHANGED, {
        product_id: currentProductId,
        searched_text: debounceSearch
      });
    }
  }, [currentProductId]);
  useDebounce(search, ANALYTICS_DEBOUNCE, reportSearchOnChangeAnalyticFn);
  const onSearchChange = e => {
    setSearch(e.target.value);
  };
  const handleAnalyticOnClick = projectParam => {
    const project = projectParam;
    const data = {
      product_ids: project.products && project.products.map(product => product.key).join(', '),
      product_names: project.products && project.products.map(product => product.name).join(', '),
      project_id: project.id,
      project_name: project.name
    };
    analytics.track(project.classification === 'template' ? ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_TEMPLATE_CLICK : ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_PROJECT_CLICK, data);
  };
  const projectsSearch = useMemo(() => projects && projects.filter(p => safeSearchRegex(search).test(p.name)) || [], [projects, search]);
  const accountsSearch = useMemo(() => accounts && accounts.filter(a => safeSearchRegex(search).test(a.account_display_name)) || [], [accounts, search]);
  const templatesSearch = useMemo(() => templates && templates.filter(t => safeSearchRegex(search).test(t.name)) || [], [templates, search]);
  const searchResults = projectsSearch.length + accountsSearch.length + templatesSearch.length;
  useEffect(() => {
    var _searchRef$current;
    return (_searchRef$current = searchRef.current) === null || _searchRef$current === void 0 ? void 0 : _searchRef$current.focus();
  }, []);
  return /*#__PURE__*/React.createElement(_StyledDiv, {
    $_css: [{
      width: 360
    }]
  }, /*#__PURE__*/React.createElement(_StyledDiv2, {
    $_css2: [{
      position: 'relative',
      padding: '10px 16px 0'
    }]
  }, /*#__PURE__*/React.createElement(TextInput, {
    "data-testid": "ProjectPicker_input",
    renderLeftElement: p => /*#__PURE__*/React.createElement(MagnifyingGlassIcon, p),
    placeholder: (_T$translate = T.translate('SEARCH')) === null || _T$translate === void 0 ? void 0 : _T$translate.toString(),
    value: search || '',
    onChange: onSearchChange,
    ref: searchRef
  }), search && /*#__PURE__*/React.createElement(XIcon, {
    size: 20,
    style: {
      position: 'absolute',
      top: 19,
      right: 25,
      color: theme.colors.charcoal500,
      cursor: 'pointer'
    },
    onClick: () => setSearch('')
  })), search && !!searchResults && /*#__PURE__*/React.createElement(_StyledDiv3, {
    $_css3: [theme.typography.heading5, {
      color: theme.colors.charcoal700,
      padding: '8px 16px'
    }]
  }, T.translate('RESULTS_FOUND', {
    context: searchResults
  })), search && !searchResults && !projectsLoading && !projectsError && /*#__PURE__*/React.createElement(_StyledDiv4, {
    $_css4: [theme.typography.bodySmall, {
      color: theme.colors.charcoal700,
      textAlign: 'center',
      padding: '36px 0'
    }]
  }, T.translate('NO_RESULTS_FOUND')), search && !searchResults && projectsLoading && /*#__PURE__*/React.createElement(ProjectRowLoader, null), search && !searchResults && projectsError && !projectsLoading && /*#__PURE__*/React.createElement(ProjectsError, {
    retry: projectsRetry,
    projectsCount: projectsSearch.length
  }), !!searchResults && /*#__PURE__*/React.createElement(VirtualizedProjectList, {
    projects: projectsSearch,
    accounts: accountsSearch,
    templates: templatesSearch,
    currentAccountId: currentAccountId,
    currentProjectId: currentProjectId,
    currentProductId: currentProductId,
    projectsLoading: projectsLoading,
    projectsError: projectsError,
    projectsRetry: projectsRetry,
    onClose: onClose,
    onAccountClick: onAccountClick,
    onProjectClick: onProjectClick,
    handleAnalyticOnClick: handleAnalyticOnClick
  }), /*#__PURE__*/React.createElement(_StyledDiv5, {
    $_css5: [{
      borderTop: "1px solid ".concat(theme.colors.charcoal200),
      padding: '14px 12px'
    }]
  }, /*#__PURE__*/React.createElement(LinkButton, {
    as: "a",
    size: LinkButton.SIZES.SMALL,
    style: theme.typography.bodyMedium,
    href: "".concat(getAccHostname(), "/projects"),
    onClick: () => {
      analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_ALLPROJECTS_CLICK);
    }
  }, T.translate('VIEW_ALL_PROJECTS'))));
};
ProjectPicker.displayName = 'ProjectPicker';
ProjectPicker.propTypes = {
  /** Available accounts to select from */
  accounts: accountsListShape,
  /** current selected account id */
  currentAccountId: PropTypes.string,
  /** Available projects to select from */
  projects: projectsListShape,
  /** Available templates */
  templates: projectsListShape,
  /** current selected project id */
  currentProjectId: PropTypes.string,
  /** current product ID, to check if project has entitlements for or not */
  currentProductId: PropTypes.string,
  /** show projects loader */
  projectsLoading: PropTypes.bool,
  /** is project entitlements ended in error */
  projectsError: PropTypes.bool,
  /** retry loading project entitlements */
  projectsRetry: PropTypes.func,
  /** menu close handler */
  onClose: PropTypes.func,
  /** account click handler */
  onAccountClick: PropTypes.func,
  /** project click handler */
  onProjectClick: PropTypes.func
};
export default ProjectPicker;
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), p => p.$_css);
var _StyledDiv2 = _styled2("div")(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["", ""])), p => p.$_css2);
var _StyledDiv3 = _styled3("div")(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["", ""])), p => p.$_css3);
var _StyledDiv4 = _styled4("div")(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["", ""])), p => p.$_css4);
var _StyledDiv5 = _styled5("div").withConfig({
  displayName: "ProjectPicker___StyledDiv5",
  componentId: "sc-1kmcv4t-0"
})(["", ""], p => p.$_css5);
//# sourceMappingURL=ProjectPicker.js.map